import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Slider from "@mui/material/Slider";
import TuneIcon from "@mui/icons-material/Tune";
import {
  fetchClientHealthConfig,
  updateClientHealthConfig,
} from "../../api/watchService";
import { vitalRangeColor } from "../../utils/vitalSeverity";

/**
 * GMA-1588 — Caregiver-admin sliders to adjust a client's vitals alert
 * thresholds. Some clients have chronic conditions, so the generic safe ranges
 * would false-alert; admins raise or lower each level per client here.
 *
 * Persists to ClientHealthConfig.customThresholds via the existing
 * GET/PUT /api/v1/users/:id/health/config. Keys match vitalsThresholdEngine:
 * `<metricType>_<bound>` with bound = warn_below | warn_above | crit_below |
 * crit_above. A key left at its baseline default is NOT persisted, so the
 * conservative baseline stays authoritative unless explicitly overridden.
 *
 * QA use (GMA-1589): tightening a level around a healthy client's normal reading
 * (e.g. SpO₂ critical-below 99) makes a real alert fire with no medical episode.
 */

// Baseline defaults mirror cortex-be vitalsThresholdEngine VITALS_DEFAULTS.
export const VITAL_THRESHOLDS = [
  {
    metric: "spo2", label: "Blood Oxygen (SpO₂)", unit: "%", min: 70, max: 100, step: 1,
    bounds: [
      { bound: "warn_below", label: "Warning below", def: 92 },
      { bound: "crit_below", label: "Critical below", def: 88 },
    ],
  },
  {
    metric: "heart_rate", label: "Heart Rate", unit: "bpm", min: 30, max: 200, step: 1,
    bounds: [
      { bound: "warn_below", label: "Warning below", def: 50 },
      { bound: "warn_above", label: "Warning above", def: 110 },
      { bound: "crit_below", label: "Critical below", def: 40 },
      { bound: "crit_above", label: "Critical above", def: 130 },
    ],
  },
  {
    metric: "resting_heart_rate", label: "Resting Heart Rate", unit: "bpm", min: 30, max: 160, step: 1,
    bounds: [
      { bound: "warn_below", label: "Warning below", def: 45 },
      { bound: "warn_above", label: "Warning above", def: 100 },
      { bound: "crit_below", label: "Critical below", def: 38 },
      { bound: "crit_above", label: "Critical above", def: 120 },
    ],
  },
  {
    metric: "walking_heart_rate", label: "Walking Heart Rate", unit: "bpm", min: 80, max: 200, step: 1,
    bounds: [
      { bound: "warn_above", label: "Warning above", def: 140 },
      { bound: "crit_above", label: "Critical above", def: 160 },
    ],
  },
  {
    metric: "respiratory_rate", label: "Respiratory Rate", unit: "brpm", min: 4, max: 40, step: 1,
    bounds: [
      { bound: "warn_below", label: "Warning below", def: 10 },
      { bound: "warn_above", label: "Warning above", def: 24 },
      { bound: "crit_below", label: "Critical below", def: 8 },
      { bound: "crit_above", label: "Critical above", def: 28 },
    ],
  },
  {
    metric: "blood_pressure_systolic", label: "Systolic Blood Pressure", unit: "mmHg", min: 90, max: 220, step: 1,
    bounds: [
      { bound: "warn_above", label: "Warning above", def: 140 },
      { bound: "crit_above", label: "Critical above", def: 180 },
    ],
  },
  {
    metric: "blood_pressure_diastolic", label: "Diastolic Blood Pressure", unit: "mmHg", min: 50, max: 140, step: 1,
    bounds: [
      { bound: "warn_above", label: "Warning above", def: 90 },
      { bound: "crit_above", label: "Critical above", def: 120 },
    ],
  },
];

export const thresholdKey = (metric, bound) => `${metric}_${bound}`;

const MANAGED_KEYS = new Set(
  VITAL_THRESHOLDS.flatMap((v) => v.bounds.map((b) => thresholdKey(v.metric, b.bound))),
);

/** Map of fullKey -> baseline default. */
export const defaultThresholdValues = () => {
  const out = {};
  for (const v of VITAL_THRESHOLDS) {
    for (const b of v.bounds) out[thresholdKey(v.metric, b.bound)] = b.def;
  }
  return out;
};

/** Effective values shown in the UI: per-client override, else baseline default. */
export const buildThresholdValues = (custom = {}) => {
  const out = {};
  for (const v of VITAL_THRESHOLDS) {
    for (const b of v.bounds) {
      const key = thresholdKey(v.metric, b.bound);
      const ov = custom?.[key];
      out[key] = typeof ov === "number" && Number.isFinite(ov) ? ov : b.def;
    }
  }
  return out;
};

/**
 * Build the customThresholds object to PUT: preserve any non-managed keys the
 * backend already stored (e.g. signalSeverity), drop managed keys equal to the
 * baseline default, and write only the genuine overrides.
 */
export const mergeCustomThresholds = (existingCustom = {}, values = {}) => {
  const merged = { ...(existingCustom || {}) };
  for (const key of MANAGED_KEYS) delete merged[key];
  const defaults = defaultThresholdValues();
  for (const key of MANAGED_KEYS) {
    const val = values[key];
    if (typeof val === "number" && Number.isFinite(val) && val !== defaults[key]) {
      merged[key] = val;
    }
  }
  return merged;
};

export default function WatchThresholdSettings({
  clientId,
  canManage = false,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(() => defaultThresholdValues());
  const [existingCustom, setExistingCustom] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadedFor = useRef(null);
  // GMA-1574 privacy guard: the pane can be reused across client switches, so a
  // late GET/PUT response for client A must never commit to client B's state.
  const isMounted = useRef(true);
  const activeClientId = useRef(clientId);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const config = await fetchClientHealthConfig(clientId);
      if (!isMounted.current || activeClientId.current !== clientId) return;
      const custom = config?.customThresholds || {};
      setExistingCustom(custom);
      setValues(buildThresholdValues(custom));
      setDirty(false);
      loadedFor.current = clientId;
    } catch (err) {
      if (!isMounted.current || activeClientId.current !== clientId) return;
      console.error("Failed to load threshold config", err);
      setLoadError(true);
    } finally {
      if (isMounted.current && activeClientId.current === clientId) setLoading(false);
    }
  }, [clientId]);

  // Reset when switching clients so one client's overrides never show on another.
  useEffect(() => {
    activeClientId.current = clientId;
    setOpen(false);
    setValues(defaultThresholdValues());
    setExistingCustom({});
    setDirty(false);
    loadedFor.current = null;
  }, [clientId]);

  // Lazy-load the saved config the first time the panel is opened per client.
  useEffect(() => {
    if (open && loadedFor.current !== clientId) load();
  }, [open, clientId, load]);

  const handleChange = (key, next) => {
    setValues((prev) => ({ ...prev, [key]: next }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (saving) return;
    // Capture the client this save targets; a profile switch mid-flight must not
    // merge this client's overrides into the next client's state (GMA-1574).
    const targetClientId = clientId;
    setSaving(true);
    try {
      const merged = mergeCustomThresholds(existingCustom, values);
      const config = await updateClientHealthConfig(targetClientId, {
        customThresholds: merged,
      });
      if (!isMounted.current || activeClientId.current !== targetClientId) return;
      setExistingCustom(config?.customThresholds || merged);
      setDirty(false);
      toast.success("Alert thresholds saved");
      // Let the vitals section refresh its own threshold copy — it no longer
      // polls the admin-only config endpoint (GMA-1588 poll fix).
      if (typeof onSaved === "function") onSaved();
    } catch (err) {
      if (!isMounted.current || activeClientId.current !== targetClientId) return;
      toast.error(err.message || "Failed to save thresholds");
    } finally {
      if (isMounted.current && activeClientId.current === targetClientId) {
        setSaving(false);
      }
    }
  };

  const handleReset = () => {
    setValues(defaultThresholdValues());
    setDirty(true);
  };

  if (!canManage) return null;

  return (
    <div data-testid="watch-threshold-settings" style={{ marginTop: "16px" }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "none",
          border: "1px solid #C8E6C9",
          borderRadius: "6px",
          padding: "6px 10px",
          fontSize: "0.85rem",
          fontWeight: 600,
          color: "#388E3C",
          cursor: "pointer",
        }}
      >
        <TuneIcon style={{ fontSize: 16 }} />
        Alert thresholds {open ? "▲" : "▼"}
      </button>

      {open ? (
        <div
          style={{
            marginTop: "10px",
            backgroundColor: "#fff",
            border: "1px solid #C8E6C9",
            borderRadius: "8px",
            padding: "14px 16px",
          }}
        >
          <p style={{ fontSize: "0.8rem", color: "#777", margin: "0 0 12px 0" }}>
            Adjust per-client warning and critical levels. Levels left at the
            default are not stored, so the safe baseline applies unless you
            override it here.
          </p>

          {loadError ? (
            <p style={{ fontSize: "0.85rem", color: "#C62828", margin: 0 }}>
              Couldn&apos;t load saved thresholds.{" "}
              <button
                type="button"
                onClick={load}
                style={{ background: "none", border: "none", color: "#388E3C", cursor: "pointer", textDecoration: "underline" }}
              >
                Retry
              </button>
            </p>
          ) : loading ? (
            <p style={{ fontSize: "0.85rem", color: "#888", margin: 0 }}>Loading thresholds…</p>
          ) : (
            <>
              {VITAL_THRESHOLDS.map((v) => (
                <div key={v.metric} style={{ marginBottom: "18px" }}>
                  <div style={{ fontSize: "0.85rem", fontWeight: 700, color: "#333", marginBottom: "4px" }}>
                    {v.label} <span style={{ color: "#999", fontWeight: 400 }}>({v.unit})</span>
                  </div>
                  {v.bounds.map((b) => {
                    const key = thresholdKey(v.metric, b.bound);
                    const critical = b.bound.startsWith("crit");
                    const bandColor = vitalRangeColor(
                      critical ? "critical" : "caution",
                      { surface: "light" },
                    );
                    return (
                      <div key={key} style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "4px" }}>
                        <span style={{ width: "120px", fontSize: "0.78rem", color: bandColor }}>
                          {b.label}
                        </span>
                        <Slider
                          data-testid={`threshold-slider-${key}`}
                          value={values[key]}
                          min={v.min}
                          max={v.max}
                          step={v.step}
                          valueLabelDisplay="auto"
                          size="small"
                          onChange={(_e, next) => handleChange(key, Array.isArray(next) ? next[0] : next)}
                          sx={{ flex: 1, color: bandColor }}
                          aria-label={`${v.label} ${b.label}`}
                        />
                        <span data-testid={`threshold-value-${key}`} style={{ width: "56px", textAlign: "right", fontSize: "0.85rem", fontWeight: 600, color: "#333" }}>
                          {values[key]}
                          {b.def !== values[key] ? (
                            <span style={{ color: "#aaa", fontWeight: 400 }}> *</span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  style={{
                    background: saving || !dirty ? "#A5D6A7" : "#4CAF50",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor: saving || !dirty ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving…" : "Save thresholds"}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  style={{
                    background: "none",
                    border: "1px solid #BDBDBD",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "0.85rem",
                    color: "#555",
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  Reset to defaults
                </button>
              </div>
              <p style={{ fontSize: "0.72rem", color: "#aaa", margin: "8px 0 0 0" }}>
                * differs from the default baseline.
              </p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
